/**
 * Preferências de interface guardadas no navegador.
 *
 * São preferências de **dispositivo**, não de conta: qual tema, se a sidebar
 * está recolhida, se uma listagem está em grade ou em lista. Não vão para o
 * backend — a mesma pessoa num monitor grande e num notebook quer respostas
 * diferentes, e nenhuma delas é dado de domínio.
 *
 * O `try/catch` em toda leitura e escrita não é excesso de zelo: em navegação
 * privada, com cookies de terceiros bloqueados ou com o armazenamento cheio, o
 * simples acesso a `localStorage` **lança**. Uma preferência que não persiste é
 * um aborrecimento; uma tela em branco por causa dela é um defeito.
 *
 * Existe desde que a terceira preferência apareceu. Duas cópias quase idênticas
 * do mesmo `try/catch` eram toleráveis; três é o ponto em que o padrão precisa
 * morar num lugar só.
 */

const PREFIXO = 'randobr:';

/** Lê uma preferência. Devolve `null` se não houver valor ou se o storage falhar. */
export function lerPreferencia(chave: string): string | null {
  try {
    return window.localStorage.getItem(PREFIXO + chave);
  } catch {
    return null;
  }
}

/** Grava uma preferência. Falha em silêncio quando o storage está indisponível. */
export function gravarPreferencia(chave: string, valor: string): void {
  try {
    window.localStorage.setItem(PREFIXO + chave, valor);
  } catch {
    // Sem persistência — a preferência vale só para esta sessão.
  }
}

/** Remove uma preferência, voltando ao padrão de quem nunca escolheu. */
export function removerPreferencia(chave: string): void {
  try {
    window.localStorage.removeItem(PREFIXO + chave);
  } catch {
    // Idem.
  }
}

/**
 * Lê uma preferência restrita a um conjunto fechado de valores.
 *
 * Um valor fora do conjunto — versão antiga do app, storage editado à mão —
 * é tratado como ausente, e não propagado como se fosse válido.
 */
export function lerPreferenciaDe<T extends string>(
  chave: string,
  valores: readonly T[],
): T | null {
  const bruto = lerPreferencia(chave);
  return bruto !== null && (valores as readonly string[]).includes(bruto) ? (bruto as T) : null;
}
